import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash-es/get';
import fabric from '../../../common/fabric';
import BlockWrapper from './shared/BlockWrapper';
import { getCachedImageURLFromImageObject } from '../../../common/images';
// import variables from '../../../common/variables';
import utils from '../../../common/utils';
import { Matrix } from '../../../common/lib/matrix';
import { ImageContext } from '../../components/Container';

/**
 * Image component
 *
 * @param props
 * @returns {XML }
 * @constructor
 */

const Image = props => {
  const imageStatus = React.useContext(ImageContext) || {};
  const { hyperlink } = props;
  const [params, setParams] = React.useState({
    ...props.params,
    scaleX: props.params.scaleX / props.groupScaleX,
    scaleY: props.params.scaleY / props.groupScaleY,
  });
  const { uploadingDate } = params.ludus;
  const [loading, setLoading] = React.useState(true);
  const [isUploading, setIsUploading] = React.useState(true);
  const [opacity, setOpacity] = React.useState(props.params.opacity || 1);
  React.useEffect(() => {
    const publicId = get(props, 'params.ludus.publicId');
    if (publicId) {
    }
    if (publicId && imageStatus[publicId]) {
      setIsUploading(true);
    } else if (!uploadingDate || (Date.now() - uploadingDate) / 1000 > 2) {
      // don't try to play the video too fast after a recent upload finished. The magic cloudinary touch by caching 404...
      setIsUploading(false);
    }
  }, [imageStatus, props.params]);

  React.useEffect(() => {
    setOpacity(props.params.opacity);
  }, [props.params.opacity]);

  const { shadow } = params;
  const isGif =
    params.ludus?.originalUrl &&
    params.ludus.originalUrl
      .split('.')
      .pop()
      .toLowerCase() === 'gif';

  const [src, setSrc] = React.useState(
    () =>
      get(params, 'ludus.localPath') ||
      getCachedImageURLFromImageObject(params, {
        cacheKey: props.appInstanceId,
        forcedVersion: null,
      }),
  );

  React.useEffect(() => {
    setParams({
      ...props.params,
      scaleX: props.params.scaleX / props.groupScaleX,
      scaleY: props.params.scaleY / props.groupScaleY,
    });
  }, [props.params]);

  React.useEffect(() => {
    if (isGif) {
      setTimeout(() => {
        setSrc(
          `${get(params, 'ludus.localPath') ||
            getCachedImageURLFromImageObject(params, {
              cacheKey: props.appInstanceId,
              forcedVersion: null,
            })}${window.location.hash}`,
        );
      }, 0);
    } else if (params.ludus?.url?.startsWith('data:')) {
      setSrc(params.ludus?.url);
    } else {
      setSrc(
        get(params, 'ludus.localPath') ||
          getCachedImageURLFromImageObject(params, {
            cacheKey: props.appInstanceId,
            forcedVersion: null,
          }),
      );
    }
  }, [params]);

  React.useEffect(() => {
    setLoading(true);
  }, [src]);

  React.useEffect(() => {
    if (!loading) {
      setOpacity(props.params.opacity);
    } else {
      setOpacity(0.1);
    }
  }, [loading]);

  // it's needed to be able to replay a non-looping gif when you switch slide

  if (!src || isUploading) {
    return null;
  }

  const groupMatrix = props.matrixGroup
    ? fabric.util.multiplyTransformMatrices(
        props.matrixGroup,
        params.ludus.transformMatrix,
      )
    : params.ludus.transformMatrix;

  const transformMatrix = fabric.util.multiplyTransformMatrices(
    [props.globalScale, 0, 0, props.globalScale, 0, 0],
    groupMatrix,
  );
  const options = fabric.util.qrDecompose(transformMatrix);

  const matrix = new Matrix(transformMatrix);
  matrix.scale(1 / options.scaleX, 1 / options.scaleY);
  const opts = matrix.decompose();
  const width = params.width * options.scaleX;
  const height = params.height * options.scaleY;
  const flipY = Math.sign(options.scaleY);
  const flipX = Math.sign(options.scaleX);
  const transform = `${
    utils.isSafari ? 'translateZ(0px)' : ''
  } translate(-50%, -50%) translate(${options.translateX}px, ${
    options.translateY
  }px) rotate(${options.angle}deg)  scale(${flipX},${flipY}) skew(${
    opts.skew.x
  }rad, ${options.skewY}deg)`;
  const rx = params.rx ? params.rx * props.globalScale : 0;
  const ry = params.ry ? params.ry * props.globalScale : 0;
  const c = params.contrast ? params.contrast / 100 + 1 : 1;
  const b = params.brightness ? params.brightness / 100 + 1 : 1;
  const s = params.saturation ? params.saturation / 100 + 1 : 1;
  const invert = params.invert ? 1 : 0;
  const hasColorCorrection = b !== 1 || c !== 1 || s !== 1 || invert !== 0;
  const shadowFilter = shadow
    ? utils.getDropShadowCSSFilterFromFabricShadow(
        shadow,
        0,
        props.globalScale,
        false,
      )
    : '';

  let filter = 'none';
  if (hasColorCorrection || shadow) {
    filter = `saturate(${s}) invert(${invert}) brightness(${b}) contrast(${c}) `;
  } else if (shadow) {
    filter = shadowFilter;
  }

  return (
    <BlockWrapper
      globalCompositeOperation={params.globalCompositeOperation}
      hyperlink={hyperlink}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width,
        height,
        transform,
        filter: shadowFilter,
      }}
    >
      <img
        style={{
          borderRadius: `${rx}px/${ry}px`,
          width,
          height,
          filter,
          opacity,
          objectFit:
            params.ludus.format === 'svg' ||
            !(params.imageCropX && params.imageCropY)
              ? 'cover'
              : 'unset',
        }}
        alt=""
        src={src}
        onLoad={() => {
          setOpacity(props.params.opacity);
          setLoading(false);
        }}
      />

      {props.children}
    </BlockWrapper>
  );
};

Image.propTypes = {
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
  appInstanceId: PropTypes.string.isRequired,
  globalScale: PropTypes.number.isRequired,
  hyperlink: PropTypes.string,
  children: PropTypes.node,
  matrixGroup: PropTypes.arrayOf(PropTypes.number),
  groupScaleX: PropTypes.number,
  groupScaleY: PropTypes.number,
};
Image.defaultProps = {
  hyperlink: null,
  children: null,
  matrixGroup: null,
};
export default Image;
